// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import {
  ModalBody,
  ComposedModal,
  Button,
  SkeletonPlaceholder,
  SkeletonText,
  Loading
} from '@carbon/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import QRCode from 'react-qr-code';
import { Copy, ShieldCheck, ShieldUser, ShieldPlus } from 'lucide-react';

import ModalManager from 'Containers/ModalManager/ModalManager';
import { NWComboBox, NWTextInput, NWNotification, AnalyticsLink } from 'Components/shared';
import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';

import LoginBackground from 'Assets/images/login-bg2.jpg';
import GooglePlayIcon from 'Assets/images/google-play.svg';
import AppStoreIcon from 'Assets/images/app-store.svg';

const StyledComposedModal = styled(ComposedModal)`
  width: 100%;
  height: 100%;
  z-index: 10000 !important;
  background: #0F233C no-repeat center url(${ LoginBackground });
  background-size: cover;

  .language {
    padding: 20px 20px 0;
    display: flex;
    justify-content: end;

    .cds--list-box__wrapper {
      width: 90px;
    }

    .cds--list-box__selection {
      display: none;
    }

    .cds--list-box__field .cds--text-input {
      padding-right: 2rem;
    }
  }

  .cds--modal-container {
    max-width: 680px;
    box-shadow: none;
    max-height: none;
    height: auto;
    background-color: #fffffff2;
    max-height: calc(100vh - 60px);
  }

  .logoContainer {
    width: 100%;
    min-width: 215px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;

    svg {
      width: 100px;
      height: 100px;
      fill: #0F233C;
    }

    p {
      padding: 20px 0 0;
      font-size: 16px;
      text-align: center;
      max-width: 450px;
    }
  }

  .messagePlaceholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 28px;
    text-align: center;
    padding: 0 20px;

    .stepSection {
      max-width: 500px;

      h4 {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        padding: 0;
      }

      .title {
        font-size: 18px;
        color: inherit;
        width: 100%;
        font-weight: 700;
        padding: 20px 0;
      }

      p {
        font-size: 0.875rem;
        line-height: 1.25rem;
        padding: 0;
      }
    }

    .downloadApp {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding-top: 20px;

      span {
        font-size: 18px;
        font-weight: 700;
      }

      .buttons {
        display: flex;
        gap: 10px;

        svg {
          width: 150px;
        }
      }
    }

    .qrCode {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;
      padding: 20px 0;

      .qrCodeImage {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 150px;
        height: 150px;
        padding: 10px;
        min-width: 120px;
        border: 1px solid #E2E2E2;
        background-color: #FFF;

        img {
          width:100%;
          height: auto;
        }
      }

      .qrCodeText {
        display: flex;
        align-items: center;
        padding: 5px 15px;
        border: 1px solid #E2E2E2;
        background-color: #FFF;
        border-radius: 5px;

        span {
          font-size: 14px;
          font-weight: 700;
          word-break: break-word;
          overflow-wrap: break-word;
        }

        svg {
          cursor: pointer;
          margin-left: 10px;
          display: inline-block;
          min-width: 20px;
        }
      }
    }

    .notificationMessage {
      padding-top: 20px;
      max-width: 500px;
    }

    .authCode {
      padding-top: 20px;
      max-width: 250px;
      width: 100%;

      input {
        font-size: 32px;
        font-weight: 600;
        height: 60px;
        text-align: center;
      }
    }
  }

  .actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 20px;
    margin: 0 auto 30px;
    max-width: 500px;
    width: 100%;

    button {
      height: 40px;
      padding: 0 30px;
      line-height: 40px;
      flex: 1;

      &.ghost {
        border: 1px solid #1F59A2;
        font-size: 0.875rem;
        color: #1F59A2;
      }
    }
  }

  .verify {
    .messagePlaceholder {
      margin-bottom: 0;
    }
  }

  .success {
    .logoContainer {
      p {
        font-size: 18px;
        font-weight: 700;
      }
    }

    .messagePlaceholder {
      p {
        max-width: 500px;
        padding: 20px 0 0;
        font-size: 18px;

        b {
          font-weight: 700;
        }
      }
    }
  }

  @media screen and (max-width: 767px) {
      .cds--modal-container {
        position: static;
        margin: 15px;
        max-height: calc(100vh - 30px);
      }

      .logoContainer {
        padding: 40px 20px 32px;
      }

      .messagePlaceholder {
        padding: 0 20px;
      }

      .downloadApp {
        .buttons {
          flex-direction: column;
        }
      }

      .actions {
        flex-direction: column;
        align-items: center;

        button {
          flex: none;
          width: 100%;
        }
      }
    }
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  margin: 0 auto !important;
  padding: 0 !important;
  width: 100%;
  flex-direction: column;
`;

const fields = {
  authCode: 'authCode',
};

const MFAModal = ({
  step,
  setStep,
  languages,
  language,
  setLanguage,
  onLogout,
  mfaKey,
  loadingMFAKey,
  onSubmit,
  submitLoading,
  rhfErrors,
  validators,
  register,
  errors,
  onRedirect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const analytics = useAnalytics();

  const copyCodeToClipboard = async code => {
    await copyToClipboard(code);
    analytics.track('copy_qr_information');
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  return (
    <ModalManager open>
      <StyledComposedModal
        className="MFAModal"
        onClose={ e => e.preventDefault() }
        preventCloseOnClickOutside
        size="md"
        theme={ theme }
      >
        <StyledModalBody theme={ theme }>
          <div className="language">
            <NWComboBox
              label="lang"
              light
              name="lang"
              id="lang"
              initialSelectedItem={ language }
              onChange={ item => setLanguage(item.selectedItem) }
              items={ languages }
            />
          </div>
          { step === 'intro' && (
            <div className="intro">
              <div className="logoContainer">
                <ShieldUser />
                <p>{ t('profile.mfaInfo') }</p>
              </div>
              <div className="messagePlaceholder">
                <div className="stepSection">
                  <h4>{ t('profile.step1') }:</h4>
                  <h5 className="title">{ t('profile.step1Title') }</h5>
                  <p>{ t('profile.step1Info') }</p>
                </div>
                <div className="downloadApp">
                  <span>Google Authenticator</span>
                  <div className="buttons">
                    <AnalyticsLink
                      to="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                      target="_blank"
                      event={ { action: 'google_auth_android_clicked' } }
                    >
                      <GooglePlayIcon />
                    </AnalyticsLink>
                    <AnalyticsLink
                      to="https://apps.apple.com/us/app/google-authenticator/id388497605"
                      target="_blank"
                      event={ { action: 'google_auth_apple_clicked' } }
                    >
                      <AppStoreIcon />
                    </AnalyticsLink>
                  </div>
                </div>
                <div className="downloadApp">
                  <span>Microsoft Authenticator</span>
                  <div className="buttons">
                    <AnalyticsLink
                      to="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                      target="_blank"
                      event={ { action: 'microsoft_auth_android_clicked' } }
                    >
                      <GooglePlayIcon />
                    </AnalyticsLink>
                    <AnalyticsLink
                      to="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                      target="_blank"
                      event={ { action: 'microsoft_auth_apple_clicked' } }
                    >
                      <AppStoreIcon />
                    </AnalyticsLink>
                  </div>
                </div>
              </div>
              <div className="actions">
                <Button
                  kind="ghost"
                  className="ghost"
                  onClick={ onLogout }
                >
                  <span>{ t('profile.logout') }</span>
                </Button>
                <Button
                  kind="danger"
                  onClick={ () => setStep('verify') }
                >
                  <span>{ t('common.next') }</span>
                </Button>
              </div>
            </div>
          )}
          { step === 'verify' && (
            <div className="verify">
              <div className="logoContainer">
                <ShieldPlus />
                <p>{ t('profile.authenticationApp') }</p>
              </div>
              <div className="messagePlaceholder">
                <div className="stepSection">
                  <h4>{ t('profile.step2') }:</h4>
                  <h5 className="title">{ t('profile.step2Title') }</h5>
                  <p>{ t('profile.step2Info') }</p>
                </div>
                <div className="qrCode">
                  { loadingMFAKey ? (
                    <>
                      <div className="qrCodeImage">
                        <SkeletonPlaceholder />
                      </div>
                      <SkeletonText />
                    </>
                  ) : (
                    <>
                      <div className="qrCodeImage">
                        <QRCode value={ mfaKey?.qr || '' } />
                      </div>
                      <div className="qrCodeText">
                        <span>
                          { mfaKey?.secret }
                        </span>
                        <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(mfaKey?.secret) } />
                      </div>
                    </>
                  ) }
                </div>
                <div className="stepSection">
                  <h4>{ t('profile.step3') }:</h4>
                  <h5 className="title">{ t('profile.step3Title') }</h5>
                  <p>{ t('profile.step3Info') }</p>
                </div>
                <NWNotification className="notificationMessage" errors={ errors } />
                <NWTextInput
                  type="number"
                  className="authCode"
                  autoComplete="off"
                  autoFocus
                  light
                  required
                  submitOnEnter={ onSubmit }
                  labelText={ t('profile.authCode') }
                  { ...sharedProps(fields.authCode) }
                />
              </div>
              <div className="actions">
                <Button
                  kind="ghost"
                  className="ghost"
                  onClick={ () => setStep('intro') }
                >
                  <span>{ t('common.back') }</span>
                </Button>
                <Button
                  kind="danger"
                  onClick={ onSubmit }
                  disabled={ loadingMFAKey || submitLoading }
                >
                  { submitLoading && <Loading active small withOverlay={ false } /> }
                  <span>{ t('common.validate') }</span>
                </Button>
              </div>
            </div>
          ) }
          { step === 'success' && (
            <div className="success">
              <div className="logoContainer">
                <ShieldCheck />
                <p>{ t('profile.mfaSuccess') }</p>
              </div>
              <div className="messagePlaceholder">
                <p dangerouslySetInnerHTML={ { __html: t('profile.mfaEmailInfo') } } />
              </div>
              <div className="actions">
                <Button
                  kind="danger"
                  onClick={ onRedirect }
                >
                  <span>{ t('profile.acknowledgement') }</span>
                </Button>
              </div>
            </div>
          ) }
        </StyledModalBody>
      </StyledComposedModal>
    </ModalManager>
  );
};

MFAModal.propTypes = {
  step: string,
  setStep: func,
  languages: array,
  language: object,
  setLanguage: func,
  onLogout: func,
  mfaKey: object,
  loadingMFAKey: bool,
  onSubmit: func,
  submitLoading: bool,
  rhfErrors: object,
  validators: object,
  register: func,
  errors: array,
  onRedirect: func,
};

MFAModal.displayName = 'MFAModalComponent';

export default MFAModal;
